import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { createContext, useContext } from "react";
import {
  CreateJournalEntryPayload,
  UpdateJournalEntryPayload,
} from "../../fay/generated";
import {
  JournalEntry,
  createJournalEntry,
  deleteJournalEntry,
  getJournalEntry,
  getJournalStreak,
  JournalStreak,
  updateJournalEntry,
  getJournalEntries,
  CursorPaginatedJournalEntriesResponse,
} from "../../fay/Api";
import { AxiosError } from "axios";
import toast from "react-hot-toast";
import { addDays, format } from "date-fns";

interface JournalEntryContextType {
  currentStreak: JournalStreak | undefined;
  isLoadingCurrentStreak: boolean;
  journalingIsEnabled: boolean;
  addJournalEntryToCache: (entry: JournalEntry) => void;
  useGetJournalEntryList: (patientId: string) => {
    entries: JournalEntry[];
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
    error: Error | null;
  };
  useGetJournalEntry: (id?: string) => {
    data: JournalEntry | undefined;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  };
  createJournalEntry: (
    payload: CreateJournalEntryPayload,
  ) => Promise<JournalEntry>;
  createJournalEntryError: string | null;
  updateJournalEntry: (
    id: string,
    payload: UpdateJournalEntryPayload,
  ) => Promise<JournalEntry>;
  updateJournalEntryError: string | null;
  deleteJournalEntry: (id: string) => Promise<void>;
  deleteJournalEntryError: string | null;
}

const JournalEntryContext = createContext<JournalEntryContextType | undefined>(
  undefined,
);

export const FoodJournalProvider: React.FC<{
  children: React.ReactNode;
  userType: "provider" | "patient";
}> = ({ children, userType }) => {
  const queryClient = useQueryClient();

  const useGetJournalEntry = (id?: string) =>
    useQuery({
      queryKey: ["journalEntry", id],
      queryFn: () => getJournalEntry(id!),
      staleTime: 1000 * 60 * 60,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      enabled: !!id,
    });

  const useGetJournalEntryList = (patientId: string = "me") =>
    useInfiniteQuery({
      queryKey: ["journalEntries", patientId],
      queryFn: async ({ pageParam }) =>
        await getJournalEntries(patientId, pageParam ?? null),
      getNextPageParam: (lastPage, _allPages) => lastPage.next_cursor,
      initialPageParam: "",
    });

  const { mutateAsync: _createJournalEntry, error: _createJournalEntryError } =
    useMutation({
      mutationFn: createJournalEntry,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["journalEntry", data.journal_entry_id],
        });
        queryClient.invalidateQueries({
          queryKey: ["streak"],
        });
        queryClient.invalidateQueries({
          queryKey: ["journalEntries", "me"],
        });
        queryClient.invalidateQueries({
          queryKey: ["journalEntries", data.patient_id],
        });
      },
    });

  let createJournalEntryError = null;
  if (_createJournalEntryError !== null) {
    if (_createJournalEntryError instanceof AxiosError) {
      if (_createJournalEntryError.response?.status === 422) {
        const detail = _createJournalEntryError.response?.data.detail[0];

        createJournalEntryError = detail.msg.split(", ")[1];
      }
    }

    if (createJournalEntryError === null) {
      createJournalEntryError = _createJournalEntryError.message;
    }
  }

  const { mutateAsync: _updateJournalEntry } = useMutation({
    mutationFn: updateJournalEntry,
    onSuccess: (newEntry, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["journalEntry", variables.id],
      });

      queryClient.setQueryData(
        ["journalEntries", "me"], // Hard coded to "me" since the only use case right now is self-edits.
        (data: {
          pageParams: any;
          pages: CursorPaginatedJournalEntriesResponse[];
        }) => {
          const newPages = data?.pages.map(
            (page) =>
              ({
                ...page,
                entries: page.entries.map((entry) => {
                  if (entry.journal_entry_id === newEntry.journal_entry_id) {
                    return newEntry;
                  }
                  return entry;
                }),
              }) ?? [],
          );

          return {
            pages: newPages,
            pageParams: data.pageParams,
          };
        },
      );
    },
  });

  const { mutateAsync: _deleteJournalEntry } = useMutation({
    mutationFn: deleteJournalEntry,
    onSuccess: (_, journalEntryId) => {
      queryClient.invalidateQueries({ queryKey: ["streak"] });
      queryClient.removeQueries({
        queryKey: ["journalEntry", journalEntryId],
      });
      queryClient.setQueryData(
        ["journalEntries", "me"], // Hard coded for now.
        (data: {
          pageParams: any;
          pages: CursorPaginatedJournalEntriesResponse[];
        }) => {
          const newPages = data?.pages.map(
            (page) =>
              ({
                ...page,
                entries: page.entries.filter((entry) => {
                  return entry.journal_entry_id !== journalEntryId;
                }),
              }) ?? [],
          );

          return {
            pages: newPages,
            pageParams: data.pageParams,
          };
        },
      );

      toast.success("Entry deleted");
    },
  });

  const { data: currentStreak, isLoading: isLoadingCurrentStreak } = useQuery({
    queryKey: ["streak"],
    queryFn: () => {
      if (userType !== "patient") {
        return Promise.resolve({
          journal_count: 0,
          consecutive_days: 0,
          start_date: null,
          weekly_streak_display: Array.from(Array(7)).map((_, i) => {
            const date = addDays(new Date(), i);

            return {
              label: format(date, "iiii"),
              filled: false,
              animate_in: false,
            };
          }),
        } satisfies JournalStreak);
      }

      return getJournalStreak();
    },
  });

  const value = {
    currentStreak,
    isLoadingCurrentStreak,
    useGetJournalEntry: (id?: string) => {
      const { data, isLoading, isError, error } = useGetJournalEntry(id);
      return { data, isLoading, isError, error };
    },
    createJournalEntryError,
    createJournalEntry: (payload: CreateJournalEntryPayload) =>
      _createJournalEntry(payload).then((data) => data),
    updateJournalEntry: (id: string, payload: UpdateJournalEntryPayload) =>
      _updateJournalEntry({ id, payload }).then((data) => data),
    updateJournalEntryError: null,
    deleteJournalEntry: (id: string) => _deleteJournalEntry(id).then(() => {}),
    deleteJournalEntryError: null,
    journalingIsEnabled: userType === "patient",
    addJournalEntryToCache: (entry: JournalEntry) => {
      const journalEntryId = entry.journal_entry_id;
      if (queryClient.getQueryData(["journalEntry", journalEntryId])) {
        return;
      }
      queryClient.setQueryData(["journalEntry", journalEntryId], entry);
    },
    useGetJournalEntryList: (patientId: string) => {
      const {
        data,
        isLoading,
        hasNextPage: hasMore,
        fetchNextPage: loadMore,
        error,
      } = useGetJournalEntryList(patientId);
      return {
        entries:
          data?.pages.reduce(
            (acc, page) => [...acc, ...page.entries],
            [] as JournalEntry[],
          ) ?? [],
        isLoading,
        hasMore,
        loadMore,
        error,
      };
    },
  };

  return (
    <JournalEntryContext.Provider value={value}>
      {children}
    </JournalEntryContext.Provider>
  );
};

// Custom hook to use the context
export const useFoodJournal = () => {
  const context = useContext(JournalEntryContext);
  if (context === undefined) {
    throw new Error("useFoodJournal must be used within a FoodJournalProvider");
  }
  return context;
};

export default FoodJournalProvider;
